import type { FC } from 'react';
import React, { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { DialogsStateContainer } from '@confluence/dialogs';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils';

import { getAutomationSlide } from './getAutomationSlide';
import { getWhiteboardsSlide } from './getWhiteboardsSlide';
import type { OnboardingSlide } from './OnboardingModal';
import { OnboardingModal } from './OnboardingModal';
import UniverseLight from './images/Universe-Light.svg';
import UniverseDark from './images/Universe-Dark.svg';
import DecisionsLight from './images/Decisions-Light.svg';
import DecisionsDark from './images/Decisions-Dark.svg';
import UnlockLight from './images/Unlock-Light.svg';
import UnlockDark from './images/Unlock-Dark.svg';
import UptimeLight from './images/Uptime-Light.svg';
import UptimeDark from './images/Uptime-Dark.svg';

export const i18n = defineMessages({
	'slide-1-title': {
		id: `premium.admin.onboarding.slide1.title`,
		description: `Title of the 1st slide for Premium admin onboarding`,
		defaultMessage: `Welcome to Confluence Cloud Premium`,
	},
	'slide-2-automation-title': {
		id: `premium.admin.onboarding.slide2.automation.title`,
		description: `Title of the NEW 2nd slide for Premium admin onboarding`,
		defaultMessage: `Automate your growing Confluence site`,
	},
	'slide-2-title': {
		id: `premium.admin.onboarding.slide2.title`,
		description: `Title of the 2nd slide for Premium admin onboarding`,
		defaultMessage: `Make better decisions`,
	},
	'slide-3-title': {
		id: `premium.admin.onboarding.slide3.title`,
		description: `Title of the 3rd slide for Premium admin onboarding`,
		defaultMessage: `Remove obstacles fast`,
	},
	'slide-5-title': {
		id: `premium.admin.onboarding.slide5.title`,
		description: `Title of the 5th slide for Premium admin onboarding`,
		defaultMessage: `Uptime, all the time`,
	},
	'slide-1-body': {
		id: `premium.admin.onboarding.slide1.body`,
		description: `Body of the 1st slide for Premium admin onboarding`,
		defaultMessage: `Scale effortlessly as your organization grows. You’ve got more visibility, controls, and platform support to take team knowledge to the next level.`,
	},
	'slide-2-automation-body': {
		id: `premium.admin.onboarding.slide2.automation.body`,
		description: `Body of the NEW 2nd slide for Premium admin onboarding.`,
		defaultMessage: `<automationLink>Automation</automationLink> lets you track the content lifecycle, keep spaces organized, and notify teams about important tasks and deadlines.`,
	},
	'slide-2-body': {
		id: `premium.admin.onboarding.slide2.body`,
		description: `Body of the 2nd slide for Premium admin onboarding. Uses id: premium.admin.onboarding.analytics.`,
		defaultMessage: `Use site, space, and page {analyticsLink} to report on project progress, team involvement, and popular content.`,
	},
	'slide-3-body': {
		id: `premium.admin.onboarding.slide3.body`,
		description: `Body of the 3rd slide for Premium admin onboarding. {temporaryAccessLink} links to super admin documentation and it's message is translated in 'premium.admin.onboarding.temporary.access'. {inspectUserPermissionsLink} links to inspect user's permissions documentation and it's message is translated in 'premium.admin.onboarding.inspect.user.permissions'`,
		defaultMessage: `Get {temporaryAccessLink} to restricted pages and {inspectUserPermissionsLink} on pages to solve access issues quickly and get your team back to work.`,
	},
	'slide-5-body': {
		id: `premium.admin.onboarding.slide5.body`,
		description: `Body of the 5th slide for Premium admin onboarding. {learnMore} links to learn about confluence cloud plans documentation and it's message is translated in 'premium.admin.onboarding.learn.more'`,
		defaultMessage: `Sleep well at night knowing that Confluence will be up and running around the clock. Premium includes a 99.9% uptime SLA, 24/7 support, and unlimited storage. {learnMore}`,
	},
	'slide-whiteboards-title': {
		id: `premium.admin.onboarding.slide.whiteboards.title`,
		description: `Title that summarizes features the new features available in Confluence Whiteboards`,
		defaultMessage: `Brainstorm with whiteboards`,
	},
	'slide-whiteboards-body': {
		id: `premium.admin.onboarding.slide.whiteboards.body`,
		description: `Body/description of the features the new features available in Confluence Whiteboards`,
		defaultMessage: `Unlock access to advanced whiteboarding features like richer integrations with Jira and smart sections! <learnMoreLink>Learn more</learnMoreLink>`,
	},
	'learn-more': {
		id: `premium.admin.onboarding.learn.more`,
		description: `Phrase that prompts the user to learn more about Confluence Premium`,
		defaultMessage: `Learn more`,
	},
	'temporary-access': {
		id: `premium.admin.onboarding.temporary.access`,
		description: `Phrase that links to a page with more information on Temporary Access`,
		defaultMessage: `temporary access`,
	},
	'inspect-user-permissions': {
		id: `premium.admin.onboarding.inspect.user.permissions`,
		description: `Phrase that links to a page with more information about inspecting user's permissions`,
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: `inspect users' permissions`,
	},
	analytics: {
		id: `premium.admin.onboarding.analytics`,
		description: `Phrase that links to a page with more information about analytics`,
		defaultMessage: `analytics`,
	},
});

type PremiumAdminOnboardingProps = {
	stop?: () => void;
};

export const PremiumAdminOnboarding: FC<PremiumAdminOnboardingProps> = ({ stop }) => {
	const [dialogOpen, setDialogOpen] = useState(false);

	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();

	const ONBOARDING_SLIDES: OnboardingSlide[] = [
		{
			title: <FormattedMessage {...i18n['slide-1-title']} />,
			body: <FormattedMessage {...i18n['slide-1-body']} />,
			imageUrlLight: UniverseLight,
			imageUrlDark: UniverseDark,
		},
		getAutomationSlide(
			i18n['slide-2-automation-title'],
			i18n['slide-2-automation-body'],
			'siteAdmin',
		),

		{
			title: <FormattedMessage {...i18n['slide-2-title']} />,
			body: (
				<FormattedMessage
					{...i18n['slide-2-body']}
					values={{
						analyticsLink: (
							<a
								href="https://confluence.atlassian.com/display/ConfCloud/Analytics"
								target="_blank"
							>
								<FormattedMessage {...i18n['analytics']} />
							</a>
						),
					}}
				/>
			),
			imageUrlLight: DecisionsLight,
			imageUrlDark: DecisionsDark,
		},
		{
			title: <FormattedMessage {...i18n['slide-3-title']} />,
			body: (
				<FormattedMessage
					{...i18n['slide-3-body']}
					values={{
						temporaryAccessLink: (
							<a
								href="https://confluence.atlassian.com/display/ConfCloud/Access+a+page+with+admin+key"
								target="_blank"
							>
								<FormattedMessage {...i18n['temporary-access']} />
							</a>
						),
						inspectUserPermissionsLink: (
							<a
								href="https://confluence.atlassian.com/display/ConfCloud/Inspect+permissions"
								target="_blank"
							>
								<FormattedMessage {...i18n['inspect-user-permissions']} />
							</a>
						),
					}}
				/>
			),
			imageUrlLight: UnlockLight,
			imageUrlDark: UnlockDark,
		},
		{
			title: <FormattedMessage {...i18n['slide-5-title']} />,
			body: (
				<FormattedMessage
					{...i18n['slide-5-body']}
					values={{
						learnMore: (
							<a href="https://confluence.atlassian.com/x/W6T0OQ" target="_blank">
								<FormattedMessage {...i18n['learn-more']} />
							</a>
						),
					}}
				/>
			),
			imageUrlLight: UptimeLight,
			imageUrlDark: UptimeDark,
		},
		...(isWhiteboardFeatureEnabled('whiteboardPaywall')
			? [
					getWhiteboardsSlide(
						i18n['slide-whiteboards-title'],
						i18n['slide-whiteboards-body'],
						'siteAdmin',
					),
				]
			: []),
	];

	return (
		<Subscribe to={[DialogsStateContainer]}>
			{(dialogs: DialogsStateContainer) => {
				if (!dialogOpen) {
					setDialogOpen(true);
					dialogs.showDialog(OnboardingModal, {
						slides: ONBOARDING_SLIDES,
						onClose: () => {
							if (stop) {
								stop(); // tells EP's CoordinationClient to stop showing onboarding again
							} else {
								throw new Error('PremiumAdminOnboarding should be a child of PremiumOnboarding');
							}
						},
					});
				}
				return null;
			}}
		</Subscribe>
	);
};
